<template>

  <div class="bot-layout">
  <PageTitle :title="t('bot')" />

    <div class="wrapper bot-layout__wrapper">
      <UserContentWrapper
        :title="t('bot.chats')"
        @back="$emit('back')"
        class="bot-layout__content"
      >

        
        <StateWrapper
          :content="chats"
          :loadingStatus="loadingStatus"
          :checkLogin="true"
        >
            <v-text-field
                  v-model="chatName"
                  :label="t('bot.new.chat')"
                  variant="outlined"
                  :hint="t('bot.enter.chat.name')"
                  rounded
                  append-icon="mdi-send"
                  @click:append="createChat"
                  :loading="isCreatingChat"
                  :disabled="isCreatingChat"
                />
          <v-card
            v-for="chat in chats"
            :key="chat.id"
            class="mx-auto ma-2 bot-layout__chat-card"
            variant="outlined"
            :subtitle="formatDate(chat.createdAt)"
            :title="chat.title"
          >
            <v-card-text>
              <p v-if="chat.history && chat.history.length > 0">
                 {{ t('bot.chat.contains.messages')}}  {{chat.history.length}}
              </p>
              <p v-else>
                {{ t('bot.chat.no.messages') }}
              </p>
            </v-card-text>

            <template v-slot:actions>
              <v-spacer></v-spacer>
              <v-btn
                variant="outlined"
                class="text-red-lighten-1 ma-2 bot-layout__delete-btn"
                @click="deleteChat(chat.id)"
                :loading="deletingChatId === chat.id"
                :disabled="deletingChatId === chat.id"
              >
                {{ t('delete') }}
              </v-btn>
              <v-btn
                variant="outlined"
                class="text-indigo-lighten-1 ma-2 bot-layout__open-btn"
                @click="router.push({ path: '/bot/' + chat.id })"
              >
                {{ t('open') }}
              </v-btn>
            </template>
          </v-card>
          
          <div class="bot-layout__pagination" v-if="!!data && data.chatsCount">
            <vue-awesome-paginate
              :total-items="data.chatsCount"
              v-model="page"
              :items-per-page="notePerPage"
              :max-pages-shown="maxPagesShown"
              @click="getMyChats"
            />
          </div>
        </StateWrapper>
      </UserContentWrapper>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { t } from '@/utils/translate';
import { usePopupStore } from '@/store/popupStore';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';
import { botApiUrl } from '@/service/user.service';
import { customAxios } from '@/service/customAxios';
import { capitalizeFirstLetter } from '@/utils/common';
import PageTitle from '@/elements/PageTitle.vue'
 import StateWrapper from "@/components/StateWrapper.vue";
 import UserContentWrapper from '@/elements/UserContentWrapper.vue';

// Store references
const router = useRouter();
const popupStore = usePopupStore();
const { showPopup } = popupStore;
const userStore = useUserStore();
const { user } = storeToRefs(userStore);

// Reactive variables
const chatName = ref('');
const chats = ref([]);
const loadingStatus = ref('loading');
const data = ref({});
const page = ref(1);
const notePerPage = ref(10);
const maxPagesShown = ref(3);
const isCreatingChat = ref(false);
const deletingChatId = ref(null);

// Computed properties
const hasChats = computed(() => chats.value && chats.value.length > 0);

// Methods
async function createChat() {
  if (!chatName.value.trim()) {
    showPopup(t('bot.chat.name.required'), 'error');
    return;
  }
  
  isCreatingChat.value = true;
  try {
    const res = await customAxios.postExternal(
      botApiUrl + '/api/chat/new',
      { title: chatName.value.trim() }
    );
    if (res.status === 201) {
      chatName.value = '';
      router.push({ path: '/bot/' + res.data.id });
    }
  } catch (e) {
    console.error('Error creating chat:', e);
    if (e.code === 'ERR_NETWORK') {
      showPopup(t('bot.network.error'), 'error');
    } else {
      showPopup(t('bot.create.chat.error'), 'error');
    }
  } finally {
    isCreatingChat.value = false;
  }
}

async function deleteChat(id) {
  deletingChatId.value = id;
  try {
    const res = await customAxios.postExternal(
      botApiUrl + '/api/chat/' + id + '/delete',
      {}
    );
    if (res.status === 202) {
      await getMyChats();
    }
  } catch (e) {
    console.error('Error deleting chat:', e);
    showPopup(t('bot.delete.chat.error'), 'error');
  } finally {
    deletingChatId.value = null;
  }
}

async function getMyChats() {
  if (!user.value) {
    return;
  }
  
  try {
    const res = await customAxios.postExternal(
      botApiUrl + '/api/chat',
      {
        page: page.value,
        notePerPage: notePerPage.value,
      }
    );
    if (res.status === 200) {
      data.value = res.data;
      chats.value = res.data.chats;
      loadingStatus.value = 'loaded';
    }
  } catch (e) {
    console.error('Error fetching chats:', e);
    if (e.code === 'ERR_NETWORK') {
      showPopup(t('bot.network.error'), 'error');
    } else {
      showPopup(t('bot.fetch.chats.error'), 'error');
    }
    loadingStatus.value = 'error';
  }
}

// Formatting functions
function formatDate(dateString) {
  const date = new Date(dateString);
  const formatter = new Intl.DateTimeFormat("ru", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
  return capitalizeFirstLetter(formatter.format(date));
}

// Watchers
watch(() => chatName.value, (newVal) => {
  chatName.value = capitalizeFirstLetter(newVal);
});

// Lifecycle
onMounted(() => {
  getMyChats();
});
</script>


<style lang="scss">
.bot-layout {
  height: 100%;

  &__wrapper {
    @include page;
  }

  &__content {
    .v-text-field {
      margin-bottom: 20px;
    }
  }

  &__chat-card {
    .v-card-text {
      min-height: 60px;
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  &__delete-btn,
  &__open-btn {
    min-width: 80px;
  }
}
</style>




